import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useUserContext } from "contexts/UserContext";
import { v4 as uuidv4 } from "uuid";

import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

import { createColumnHelper } from "@tanstack/react-table";
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";

import mainStyle from "../mainStyle.module.scss";

import { ErrorNotification } from "pages/System/UserManagement/ErrorHandle";

import {
  Card,
  Box,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";

import { SolidButton } from "components/buttons/SolidButton";
import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import DrawerField from "components/Drawer";
import LinkModal from "components/LinkModal";

import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";
import { ReactComponent as AvatarNoneIcon } from "assets/images/icons/img_photo_none.svg";

import EditInvitation from "./Drawer/EditUser/Invitation";
import EditMemberUser from "./Drawer/EditUser/Member";
import { isBasicLimitUser } from "./licenseLimit";
import DeleteUserModal from "./Modal/DeleteUser";
import ResendModal from "./Modal/Resend";
import InviteUserModal from "./Modal/InviteUser";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;
const BASIC_PRIVILEGED_USER_LIMIT = 6;

const OrgUserManagement = ({ entId, orgId }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { scopeData, scopeReady } = useOutletContext();

  const [userDirectAccessData, setUserDirectAccessData] = useState([]);
  const [userMspAccessData, setUserMspAccessData] = useState([]);
  const [userOrgPendingData, setUserOrgPendingData] = useState([]);
  const [combinedUserData, setCombinedUserData] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [selectedRowData, setSelectedRowData] = useState(null);

  const [openDeleteAccountModal, setOpenDeleteAccountModal] =
    useState(false);

  const [openResendModal, setOpenResendModal] = useState(false);

  const [openInviteUserModal, setOpenInviteUserModal] = useState(false);

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const [unityProSiteCount, setUnityProSiteCount] = useState(0);

  const [unityProSiteIds, setUnityProSiteIds] = useState([]);

  const [isLicenseStatusLoading, setIsLicenseStatusLoading] =
    useState(true);

  const [tabValue, setTabValue] = useState("direct");

  const [errorMessageHandleModal, setErrorMessageHandleModal] =
    useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [isLoading, setIsLoading] = useState(true);

  const [tempDirectAccess, setTempDirectAccess] = useState(null);

  const [tempMspAccess, setTempMspAccess] = useState(null);

  const [tempOrgPending, setTempOrgPending] = useState(null);

  const stored = localStorage.getItem("selectBreadcrumbs");

  const [currentUserOrgRoleName, setCurrentUserOrgRoleName] =
    useState(null);

  const [currentUserOrgRoleId, setCurrentUserOrgRoleId] =
    useState(null);

  const [originalRowData, setOriginalRowData] = useState(null);

  const {
    dataMap,
    getData,
    sendRequest,
  } = useDataSourceManager();

  const supportSession = JSON.parse(
    sessionStorage.getItem("supportSession") || "null",
  );

  const accessLevel = supportSession?.accessLevel;

  const isSupportMode =
    accessLevel === "full_control" ||
    accessLevel === "read_only";

  const { userState } = useUserContext();

  const isSupporter =
    userState?.properties?.is_supporter === true;

  useEffect(() => {
    if (!stored) {
      return;
    }

    const parsed = JSON.parse(stored);

    const hasManageOrg =
      parsed?.realm?.hasManageOrg;

    if (hasManageOrg) {
      setCurrentUserOrgRoleName(
        parsed?.orgs?.roleName ?? null,
      );

      setCurrentUserOrgRoleId(
        parsed?.orgs?.roleId ?? null,
      );
    } else {
      setCurrentUserOrgRoleName(
        parsed?.realm?.roleName ?? null,
      );

      setCurrentUserOrgRoleId(
        parsed?.realm?.roleId ?? null,
      );
    }
  }, [stored]);

  const breadcrumbs = JSON.parse(
    localStorage.getItem("selectBreadcrumbs") || "null",
  );

  const hasManageOrg =
    breadcrumbs?.realm?.hasManageOrg === true;

  const isViewer =
    currentUserOrgRoleName === "Viewer" ||
    currentUserOrgRoleName === "None";

  const orgRoleHierarchy = useMemo(() => {
    const roles = [
      {
        orgRoleId: 5,
        orgRoleName: "Super Admin",
      },
      {
        orgRoleId: 6,
        orgRoleName: "Admin",
      },
      {
        orgRoleId: 7,
        orgRoleName: "Viewer",
      },
    ];

    if (
      currentUserOrgRoleName === "Super Admin"
    ) {
      return roles;
    }

    if (
      currentUserOrgRoleName === "Admin"
    ) {
      return roles.filter(
        (role) =>
          role.orgRoleName === "Admin" ||
          role.orgRoleName === "Viewer",
      );
    }

    return [];
  }, [currentUserOrgRoleName]);

  const siteRoleHierarchy = [
    {
      siteRoleId: 9,
      siteRoleName: "Admin",
    },
    {
      siteRoleId: 10,
      siteRoleName: "Viewer",
    },
  ];

  const [listOrgUsersUuid] = useState(uuidv4());

  const [listPendingOrgUsersUuid] =
    useState(uuidv4());

  const [licenseServiceStatusUuid] = useState(uuidv4());

  const getListOrgUsers = () => {
    if (hasManageOrg && orgId) {
      sendRequest(
        "/ents/:entId/orgs/:orgId/users.read",
        listOrgUsersUuid,
        {
          entId,
          orgId,
        },
      );

      return;
    }

    sendRequest(
      "/orgs/:orgId/users.read",
      listOrgUsersUuid,
      {
        orgId,
      },
    );
  };

  const getListPendingOrgUsers = () => {
    if (hasManageOrg && orgId) {
      sendRequest(
        "/ents/:entId/orgs/:orgId/invitations.read",
        listPendingOrgUsersUuid,
        {
          entId,
          orgId,
        },
      );

      return;
    }

    sendRequest(
      "/orgs/:orgId/invitations.read",
      listPendingOrgUsersUuid,
      {
        orgId,
      },
    );
  };

  const getLicenseServiceStatus = () => {
    const resourcePath = hasManageOrg && orgId
      ? "/ents/:entId/orgs/:orgId/license/service/status.read"
      : "/orgs/:orgId/license/service/status.read";

    const restfulParams = hasManageOrg && orgId
      ? { entId, orgId }
      : { orgId };

    sendRequest(resourcePath, licenseServiceStatusUuid, restfulParams);
  };

  useEffect(() => {
    if (
      checkMockup === "true" ||
      scopeReady
    ) {
      getListOrgUsers();
      getLicenseServiceStatus();
    }
  }, [checkMockup, scopeReady]);

  useEffect(() => {
    const orgUsersResponse =
      getData(listOrgUsersUuid);

    const pendingUsersResponse =
      getData(listPendingOrgUsersUuid);

    if (orgUsersResponse?.data) {
      setTempDirectAccess(
        orgUsersResponse.data
          .directAccesses || [],
      );

      setTempMspAccess(
        orgUsersResponse.data
          .mspAccesses || [],
      );

      getListPendingOrgUsers();
    }

    if (pendingUsersResponse?.data) {
      setTempOrgPending(
        pendingUsersResponse.data
          .invitations || [],
      );
    }
  }, [dataMap]);

  useEffect(() => {
    const licenseStatusResponse = getData(licenseServiceStatusUuid);

    if (!licenseStatusResponse) {
      return;
    }

    const sites = licenseStatusResponse?.data?.org?.sites || [];

    const subscribedSiteIds = sites
      .filter((site) => site?.services?.unityPro === true)
      .map((site) => site.id);

    setUnityProSiteCount(subscribedSiteIds.length);
    setUnityProSiteIds(subscribedSiteIds);
    setIsLicenseStatusLoading(false);
  }, [dataMap]);

  useEffect(() => {
    const isAllLoaded =
      tempDirectAccess !== null &&
      tempMspAccess !== null &&
      tempOrgPending !== null;

    if (!isAllLoaded) {
      return;
    }

    setUserDirectAccessData(
      tempDirectAccess,
    );

    setUserMspAccessData(
      tempMspAccess,
    );

    setUserOrgPendingData(
      tempOrgPending,
    );

    setIsLoading(false);
  }, [
    tempDirectAccess,
    tempMspAccess,
    tempOrgPending,
  ]);

  const basicPrivilegedUserCount = useMemo(
    () => combinedUserData.filter(isBasicLimitUser).length,
    [combinedUserData],
  );

  const isPendingStatus = (
    timeOfInvitation,
  ) => {
    if (!timeOfInvitation) {
      return "-";
    }

    const now =
      Math.floor(Date.now() / 1000);

    const sevenDaysInSeconds =
      7 * 24 * 60 * 60;

    return (
      now - timeOfInvitation <=
        sevenDaysInSeconds
        ? "pending"
        : "expired"
    );
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const combined = [
      ...userDirectAccessData,

      ...userOrgPendingData.map(
        (invitation) => ({
          ...invitation,

          status: isPendingStatus(
            invitation.timeOfInvitation,
          ),
        }),
      ),
    ];

    const filtered =
      combined.filter(
        (user) =>
          user.orgRoleName &&
          user.orgRoleName !== "None",
      );

    setCombinedUserData(filtered);
  }, [
    userDirectAccessData,
    userOrgPendingData,
    isLoading,
  ]);

  const formatStatus = (status) => {
    switch (
    status?.toLowerCase()
    ) {
      case "active":
        return "Active";

      case "inactive":
        return "Inactive";

      case "expired":
        return "Expired";

      case "pending":
        return "Pending";

      default:
        return status;
    }
  };

  const allSitesName =
    useMemo(() => {
      const sites = (
        scopeData.site ?? []
      ).map((site) => ({
        siteId: site.id,
        siteName: site.name,
        siteRoleId: site.roleId,
        siteRoleName: site.roleName,
      }));

      return sites.sort((a, b) =>
        a.siteName.localeCompare(
          b.siteName,
        ),
      );
    }, [scopeData.site]);

  const handleTabChange = (
    event,
    newValue,
  ) => {
    setTabValue(newValue);
  };

  const getInitials = (name) => {
    if (
      !name ||
      typeof name !== "string"
    ) {
      return "?";
    }

    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  const handleOpenResendModal = (
    rowIndex,
    rowData,
  ) => {
    setOpenResendModal(true);

    if (rowData) {
      setSelectedRowData(rowData);
    }
  };

  const handleOpenInviteUserModal =
    () => {
      if (
        currentUserOrgRoleName ===
        "Viewer" ||
        currentUserOrgRoleName ===
        "None"
      ) {
        return;
      }

      setOpenInviteUserModal(true);
    };

  const handleCloseDeleteAccountModal =
    () => {
      setOpenDeleteAccountModal(false);
    };

  const handleCloseResendModal = () => {
    setOpenResendModal(false);
  };

  const handleCloseInviteUserModal =
    () => {
      setOpenInviteUserModal(false);
    };

  const handleBasicUserLimitReached = () => {
    setIsLinkModalOpen(true);
  };

  const handleDrawerClose = () => {
    setSelectedRowData({});

    setDrawerOpen(false);
  };

  const directColumnHelper =
    createColumnHelper();

  const directColumnsData = [
    directColumnHelper.accessor(
      "name",
      {
        header: "Name",

        meta: {
          withRightBorder: true,
        },

        cell: (info) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {info.row.original
              .avatar ? (
              <img
                src={
                  info.row.original
                    .avatar
                }
                alt="User"
                className={
                  mainStyle[
                  "avatar-with-photo"
                  ]
                }
              />
            ) : info.getValue() ? (
              <div
                className={
                  mainStyle[
                  "avatar-without-photo-has-name"
                  ]
                }
              >
                {getInitials(
                  info.getValue(),
                )}
              </div>
            ) : (
              <div
                className={
                  mainStyle[
                  "avatar-without-photo"
                  ]
                }
              >
                <AvatarNoneIcon />
              </div>
            )}

            <EllipsisTooltipCell
              text={info.getValue()}
            />
          </Box>
        ),
      },
    ),

    directColumnHelper.accessor(
      "email",
      {
        header: "Account",

        cell: (info) => (
          <EllipsisTooltipCell
            text={info.getValue()}
          />
        ),
      },
    ),

    directColumnHelper.accessor(
      "status",
      {
        header:
          "Account Status",

        cell: (info) => (
          <span
            style={{
              color:
                info.getValue() ===
                  "pending" ||
                  info.getValue() ===
                  "expired"
                  ? theme.palette
                    .primary
                    .redPrimary
                  : info.getValue() ===
                    "active"
                    ? theme.palette
                      .primary
                      .greenPrimary
                    : info.getValue() ===
                      "inactive"
                      ? theme.palette
                        .primary
                        .greySecound
                      : theme.palette
                        .primary
                        .greyPrimary,

              border: `1.5px solid ${info.getValue() ===
                  "pending" ||
                  info.getValue() ===
                  "expired"
                  ? theme.palette
                    .primary
                    .redPrimary
                  : info.getValue() ===
                    "active"
                    ? theme.palette
                      .primary
                      .greenPrimary
                    : info.getValue() ===
                      "inactive"
                      ? theme.palette
                        .primary
                        .greySecound
                      : theme.palette
                        .primary
                        .greyPrimary
                }`,

              padding:
                "4px 12px",

              width: "91px",

              height: "27px",

              gap: "10px",

              borderRadius:
                theme.shape
                  .customBorderRadius[
                7
                ],

              justifyContent:
                "center",

              alignItems:
                "center",

              textAlign:
                "center",

              display: "flex",
            }}
          >
            {info.getValue() ===
              undefined
              ? "-"
              : formatStatus(
                info.getValue(),
              )}
          </span>
        ),
      },
    ),

    directColumnHelper.accessor(
      "orgRoleName",
      {
        header: "Org Role",
      },
    ),

    directColumnHelper.accessor(
      "siteAccesses",
      {
        header:
          "Site Privilege",

        cell: (info) => {
          return (
            <Box>
              {info.row.original
                .orgRoleName ===
                "Super Admin" ||
                info.row.original
                  .orgRoleName ===
                "Admin" ? (
                <div>
                  All / Admin
                </div>
              ) : info.getValue()
                ?.length > 0 ? (
                info.getValue()
                  .length > 3 ? (
                  <Tooltip
                    title={
                      <div>
                        {info
                          .getValue()
                          .map(
                            (
                              s,
                              index,
                            ) => (
                              <div
                                key={
                                  index
                                }
                              >
                                {`${s.siteName} / ${s.siteRoleName}`}
                              </div>
                            ),
                          )}
                      </div>
                    }
                    placement="right"
                    arrow
                  >
                    <div>
                      {info
                        .getValue()
                        .slice(
                          0,
                          3,
                        )
                        .map(
                          (
                            s,
                            index,
                          ) => (
                            <div
                              key={
                                index
                              }
                            >
                              {`${s.siteName} / ${s.siteRoleName}`}
                            </div>
                          ),
                        )}

                      <div>
                        ...
                      </div>
                    </div>
                  </Tooltip>
                ) : (
                  <div>
                    {info
                      .getValue()
                      .map(
                        (
                          s,
                          index,
                        ) => (
                          <div
                            key={
                              index
                            }
                          >
                            {`${s.siteName} / ${s.siteRoleName}`}
                          </div>
                        ),
                      )}
                  </div>
                )
              ) : (
                "-"
              )}
            </Box>
          );
        },
      },
    ),

    directColumnHelper.accessor(
      "lastLoginTime",
      {
        header:
          "Last Login",

        cell: (info) => (
          <span>
            {info.getValue()
              ? dayjs
                .unix(
                  info.getValue(),
                )
                .format(
                  "YYYY/MM/DD HH:mm:ss",
                )
              : "-"}
          </span>
        ),
      },
    ),

    directColumnHelper.accessor(
      "timeOfAuth",
      {
        header: "Joined On",

        cell: (info) => (
          <span>
            {dayjs
              .unix(
                info.getValue() ||
                info.row
                  .original
                  .timeOfInvitation,
              )
              .format(
                "YYYY/MM/DD HH:mm:ss",
              )}
          </span>
        ),
      },
    ),
  ];

  const directTable =
    useTableInstance({
      tableName:
        "orgDirectUserManagement",

      data: combinedUserData,

      columns:
        directColumnsData,

      enableSorting: true,

      enablePagination: true,

      enableGlobalFilter:
        true,

      enableRowSelection:
        true,
    });

  const mspColumnHelper =
    createColumnHelper();

  const mspColumnsData = [
    mspColumnHelper.accessor(
      "name",
      {
        header: "Name",

        meta: {
          withRightBorder: true,
        },

        cell: (info) => (
          <Box
            sx={{
              display: "flex",

              alignItems:
                "center",

              gap: 1,
            }}
          >
            {info.row.original
              .avatar ? (
              <img
                src={
                  info.row.original
                    .avatar
                }
                alt="User"
                className={
                  mainStyle[
                  "avatar-with-photo"
                  ]
                }
              />
            ) : info.getValue() ? (
              <div
                className={
                  mainStyle[
                  "avatar-without-photo-has-name"
                  ]
                }
              >
                {getInitials(
                  info.getValue(),
                )}
              </div>
            ) : (
              <div
                className={
                  mainStyle[
                  "avatar-without-photo"
                  ]
                }
              >
                <AvatarNoneIcon />
              </div>
            )}

            {info.getValue() ? (
              <span>
                {info.getValue()}
              </span>
            ) : (
              "-"
            )}
          </Box>
        ),
      },
    ),

    mspColumnHelper.accessor(
      "email",
      {
        header: "Account",
      },
    ),

    mspColumnHelper.accessor(
      "orgRoleName",
      {
        header:
          "Organization Privilege",
      },
    ),
  ];

  const mspTable =
    useTableInstance({
      tableName:
        "mspDirectUserManagement",

      data:
        userMspAccessData,

      columns:
        mspColumnsData,

      enableSorting: true,

      enablePagination: true,

      enableGlobalFilter:
        true,
    });

  return (
    <>
      <Card
        className={
          mainStyle["card"]
        }
      >
        <Box
          style={{
            display: "flex",

            justifyContent:
              "space-between",

            alignItems:
              "center",

            padding:
              "10px 20px",
          }}
        >
          <Box>
            {hasManageOrg && (
              <Tabs
                value={tabValue}
                onChange={
                  handleTabChange
                }
                TabIndicatorProps={{
                  className:
                    mainStyle[
                    "tab-indicator"
                    ],
                }}
                className={
                  mainStyle[
                  "custom-tabs"
                  ]
                }
              >
                <Tab
                  label="Direct Access"
                  value="direct"
                />

                <Tab
                  label="MSP Access"
                  value="msp"
                />
              </Tabs>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor:
                  theme.palette
                    .primary
                    .greyThread,

                minHeight:
                  "50px",

                marginRight:
                  "10px",
              }}
            />

            <Box
              sx={{
                display: "flex",

                padding:
                  "3px 10px 3px",

                marginRight: "5px",
              }}
            >
              <SearchInput
                value={
                  tabValue ===
                    "direct"
                    ? directTable.globalFilter
                    : mspTable.globalFilter
                }
                onChange={
                  tabValue ===
                    "direct"
                    ? directTable.setGlobalFilter
                    : mspTable.setGlobalFilter
                }
              />
            </Box>

            {!isViewer && (
              <SolidButton
                startIcon={
                  <AddIcon />
                }
                width="130px"
                height="32px"
                onClick={
                  isSupportMode ||
                    isSupporter
                    ? undefined
                    : handleOpenInviteUserModal
                }
                disabled={
                  isSupportMode ||
                  isSupporter ||
                  isLicenseStatusLoading
                }
                sx={{
                  ...((isSupportMode ||
                    isSupporter ||
                    isLicenseStatusLoading) && {
                    filter:
                      "grayscale(100%)",

                    cursor:
                      "not-allowed",

                    pointerEvents:
                      "none",
                  }),
                }}
              >
                Invite User
              </SolidButton>
            )}
          </Box>
        </Box>

        {tabValue ===
          "direct" && (
            <Box
              style={{
                padding:
                  "10px 20px",
              }}
            >
              {isLoading ? (
                <Box
                  sx={{
                    display:
                      "flex",

                    justifyContent:
                      "center",

                    alignItems:
                      "center",

                    height:
                      "100%",

                    minHeight:
                      "400px",
                  }}
                >
                  <CircularProgress
                    sx={{
                      color:
                        theme
                          .palette
                          .primary
                          .mainHover,
                    }}
                  />
                </Box>
              ) : (
                <Box margin="20px 0">
                  <TableField
                    {...directTable}
                    drawerOpen={
                      drawerOpen
                    }
                    handleRowOnClick={(
                      row,
                    ) => {
                      setOriginalRowData(
                        row,
                      );

                      setSelectedRowData(
                        row,
                      );

                      setDrawerOpen(
                        true,
                      );
                    }}
                  />

                  {drawerOpen && (
                    <>
                      {selectedRowData?.status ===
                        "pending" ||
                        selectedRowData?.status ===
                        "expired" ? (
                        <DrawerField
                          open={
                            drawerOpen
                          }
                          handleClose={
                            handleDrawerClose
                          }
                          contentKey={`OrgUserInvitation_${uuidv4()}`}
                        >
                          <EditInvitation
                            setSelectedRowData={
                              setSelectedRowData
                            }
                            selectedRowData={
                              selectedRowData
                            }
                            userOrgPendingData={
                              userOrgPendingData
                            }
                            setUserOrgPendingData={
                              setUserOrgPendingData
                            }
                            drawerOpen={
                              drawerOpen
                            }
                            setDrawerOpen={
                              setDrawerOpen
                            }
                            getInitials={
                              getInitials
                            }
                            setOpenDeleteAccountModal={
                              setOpenDeleteAccountModal
                            }
                            handleOpenResendModal={
                              handleOpenResendModal
                            }
                            allSitesName={
                              allSitesName
                            }
                            orgRoleHierarchy={
                              orgRoleHierarchy
                            }
                            siteRoleHierarchy={
                              siteRoleHierarchy
                            }
                            currentUserOrgRoleId={
                              currentUserOrgRoleId
                            }
                            currentUserOrgRoleName={
                              currentUserOrgRoleName
                            }
                            setCurrentUserOrgRoleId={
                              setCurrentUserOrgRoleId
                            }
                            entId={
                              entId
                            }
                            orgId={
                              orgId
                            }
                            getListOrgUsers={
                              getListOrgUsers
                            }
                            getListPendingOrgUsers={
                              getListPendingOrgUsers
                            }
                            isViewer={
                              isViewer
                            }
                            formatStatus={
                              formatStatus
                            }
                            originalRowData={
                              originalRowData
                            }
                            combinedUserData={combinedUserData}
                            unityProSiteIds={unityProSiteIds}
                            unityProSiteCount={unityProSiteCount}
                            basicPrivilegedUserLimit={BASIC_PRIVILEGED_USER_LIMIT}
                            onBasicUserLimitReached={handleBasicUserLimitReached}
                          />
                        </DrawerField>
                      ) : (
                        <DrawerField
                          open={
                            drawerOpen
                          }
                          handleClose={
                            handleDrawerClose
                          }
                          contentKey={`OrgUser_${uuidv4()}`}
                        >
                          <EditMemberUser
                            setSelectedRowData={
                              setSelectedRowData
                            }
                            selectedRowData={
                              selectedRowData
                            }
                            drawerOpen={
                              drawerOpen
                            }
                            setDrawerOpen={
                              setDrawerOpen
                            }
                            getInitials={
                              getInitials
                            }
                            setOpenDeleteAccountModal={
                              setOpenDeleteAccountModal
                            }
                            orgRoleHierarchy={
                              orgRoleHierarchy
                            }
                            siteRoleHierarchy={
                              siteRoleHierarchy
                            }
                            currentUserOrgRoleId={
                              currentUserOrgRoleId
                            }
                            currentUserOrgRoleName={
                              currentUserOrgRoleName
                            }
                            setCurrentUserOrgRoleId={
                              setCurrentUserOrgRoleId
                            }
                            allSitesName={
                              allSitesName
                            }
                            entId={
                              entId
                            }
                            orgId={
                              orgId
                            }
                            getListOrgUsers={
                              getListOrgUsers
                            }
                            getListPendingOrgUsers={
                              getListPendingOrgUsers
                            }
                            isViewer={
                              isViewer
                            }
                            setCurrentUserOrgRoleName={
                              setCurrentUserOrgRoleName
                            }
                            formatStatus={
                              formatStatus
                            }
                            originalRowData={
                              originalRowData
                            }
                            combinedUserData={combinedUserData}
                            unityProSiteIds={unityProSiteIds}
                            unityProSiteCount={unityProSiteCount}
                            basicPrivilegedUserLimit={BASIC_PRIVILEGED_USER_LIMIT}
                            onBasicUserLimitReached={handleBasicUserLimitReached}
                          />
                        </DrawerField>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}

        {tabValue === "msp" && (
          <Box margin="20px 0">
            {isLoading ? (
              <Box
                sx={{
                  display:
                    "flex",

                  justifyContent:
                    "center",

                  alignItems:
                    "center",

                  height:
                    "100%",

                  minHeight:
                    "400px",
                }}
              >
                <CircularProgress
                  sx={{
                    color:
                      theme
                        .palette
                        .primary
                        .mainHover,
                  }}
                />
              </Box>
            ) : (
              <Box marginTop="20px">
                <TableField
                  {...mspTable}
                />
              </Box>
            )}
          </Box>
        )}
      </Card>

      <DeleteUserModal
        openDeleteAccountModal={
          openDeleteAccountModal
        }
        handleCloseDeleteAccountModal={
          handleCloseDeleteAccountModal
        }
        selectedRowData={
          selectedRowData
        }
        entId={entId}
        orgId={orgId}
        getListOrgUsers={
          getListOrgUsers
        }
        getListPendingOrgUsers={
          getListPendingOrgUsers
        }
        setDrawerOpen={
          setDrawerOpen
        }
      />

      <ResendModal
        openResendModal={
          openResendModal
        }
        handleCloseResendModal={
          handleCloseResendModal
        }
        selectedRowData={
          selectedRowData
        }
        entId={entId}
        orgId={orgId}
        getListOrgUsers={
          getListOrgUsers
        }
        getListPendingOrgUsers={
          getListPendingOrgUsers
        }
      />

      <InviteUserModal
        openInviteUserModal={
          openInviteUserModal
        }
        setOpenInviteUserModal={
          setOpenInviteUserModal
        }
        handleCloseInviteUserModal={
          handleCloseInviteUserModal
        }
        allSitesName={
          allSitesName
        }
        orgRoleHierarchy={
          orgRoleHierarchy
        }
        siteRoleHierarchy={
          siteRoleHierarchy
        }
        currentUserOrgRoleId={
          currentUserOrgRoleId
        }
        currentUserOrgRoleName={
          currentUserOrgRoleName
        }
        entId={entId}
        orgId={orgId}
        getListOrgUsers={
          getListOrgUsers
        }
        getListPendingOrgUsers={
          getListPendingOrgUsers
        }
        unityProSiteCount={unityProSiteCount}
        unityProSiteIds={unityProSiteIds}
        basicPrivilegedUserCount={basicPrivilegedUserCount}
        basicPrivilegedUserLimit={BASIC_PRIVILEGED_USER_LIMIT}
        onBasicUserLimitReached={handleBasicUserLimitReached}
      />

      <LinkModal
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        title="Upgrade to Nuclias Unity Pro"
        content={`Basic supports up to ${BASIC_PRIVILEGED_USER_LIMIT} users. You currently have ${combinedUserData.length} users. Upgrade to Nuclias Unity Pro to add more users.`}
        linkText="Go to License Management"
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          navigate("/license");
        }}
      />

      <ErrorNotification
        open={
          errorMessageHandleModal
        }
        onClose={() =>
          setErrorMessageHandleModal(
            false,
          )
        }
        message={
          errorMessage
        }
      />
    </>
  );
};

export default OrgUserManagement;
